import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  IncidentCategory,
  IncidentSeverity,
  ReportStatus,
} from '@dsr/shared-types';
import { useIncidents, useCreateIncident } from '../api/queries/incidents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Button,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { fmtDateTime } from '../utils/date';
import './reports.css';

export const CATEGORY_LABEL: Record<IncidentCategory, string> = {
  trespass: 'Trespass',
  theft: 'Theft',
  assault: 'Assault',
  medical: 'Medical',
  fire: 'Fire',
  suspicious_activity: 'Suspicious activity',
  access_violation: 'Access violation',
  property_damage: 'Property damage',
  protester: 'Protester',
  threat: 'Threat',
  use_of_force: 'Use of force',
  other: 'Other',
};

const CATEGORIES = Object.keys(CATEGORY_LABEL) as IncidentCategory[];

export const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  info: 'Info',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SEVERITIES: IncidentSeverity[] = ['info', 'low', 'medium', 'high', 'critical'];

export const STATUS_LABEL: Record<ReportStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUSES: ReportStatus[] = [
  'draft',
  'submitted',
  'under_review',
  'approved',
  'rejected',
];

export function SeverityBadge({ severity }: { severity: IncidentSeverity }): JSX.Element {
  return <span className={`rpt-sev rpt-sev-${severity}`}>{SEVERITY_LABEL[severity]}</span>;
}

export function StatusBadge({ status }: { status: ReportStatus }): JSX.Element {
  return <span className={`rpt-badge rpt-badge-${status}`}>{STATUS_LABEL[status]}</span>;
}

// Pull the first human-readable field error out of an ApiError, falling back
// to a generic message. Shared shape across the report pages.
export function firstFieldError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return "You don't have permission to do that.";
  }
  if (err instanceof ApiError && err.status === 400) {
    const body = err.body as
      | { details?: { fieldErrors?: Record<string, string[]> } }
      | null;
    const fe = body?.details?.fieldErrors;
    if (fe) {
      const first = Object.values(fe).find((arr) => Array.isArray(arr) && arr.length > 0);
      if (first && Array.isArray(first) && first[0]) return first[0];
    }
  }
  return fallback;
}

export function IncidentsPage(): JSX.Element {
  const navigate = useNavigate();
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<IncidentCategory | 'all'>('all');

  const { data, isPending, error } = useIncidents({
    ...(severityFilter !== 'all' ? { severity: severityFilter } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
  });

  const createIncident = useCreateIncident();
  const [showNew, setShowNew] = useState(false);
  const [newCategory, setNewCategory] = useState<IncidentCategory>('suspicious_activity');
  const [newSeverity, setNewSeverity] = useState<IncidentSeverity>('low');
  const [newOccurredAt, setNewOccurredAt] = useState<number | null>(Date.now());
  const [newNarrative, setNewNarrative] = useState('');
  const [newReportNumber, setNewReportNumber] = useState('');
  const [newPoliceNotified, setNewPoliceNotified] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const resetForm = (): void => {
    setNewCategory('suspicious_activity');
    setNewSeverity('low');
    setNewOccurredAt(Date.now());
    setNewNarrative('');
    setNewReportNumber('');
    setNewPoliceNotified(false);
    setFormError(null);
  };

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await createIncident.mutateAsync({
        category: newCategory,
        severity: newSeverity,
        occurred_at: newOccurredAt ?? undefined,
        narrative: newNarrative.trim() || undefined,
        report_number: newReportNumber.trim() || undefined,
        police_notified: newPoliceNotified,
      });
      toast.success('Incident logged.');
      setShowNew(false);
      resetForm();
      navigate(`/incidents/${res.incident.id}`);
    } catch (err) {
      setFormError(firstFieldError(err, 'Could not save the incident.'));
    }
  };

  if (isPending) return <p className="rpt-status-line">Loading incidents…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view incidents."
        : 'Failed to load incidents.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const incidents = data?.incidents ?? [];

  const severityFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All severities' },
    ...SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABEL[s] })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];
  const categoryFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All categories' },
    ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>Incidents</h1>
          <p className="page-sub">Security occurrences and their approval status.</p>
        </div>
        {can('incident.write') && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setShowNew(true)}>
              Report incident
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <div className="rpt-filter">
          <Select
            value={severityFilter}
            onValueChange={(v) => setSeverityFilter(v as IncidentSeverity | 'all')}
            options={severityFilterOptions}
            aria-label="Filter by severity"
          />
        </div>
        <div className="rpt-filter">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as ReportStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </div>
        <div className="rpt-filter">
          <Select
            value={categoryFilter}
            onValueChange={(v) => setCategoryFilter(v as IncidentCategory | 'all')}
            options={categoryFilterOptions}
            aria-label="Filter by category"
          />
        </div>
      </div>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>Occurred</Table.HeaderCell>
            <Table.HeaderCell>Category</Table.HeaderCell>
            <Table.HeaderCell>Severity</Table.HeaderCell>
            <Table.HeaderCell>Report #</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {incidents.length === 0 ? (
            <Table.Empty colSpan={5}>No incidents match these filters.</Table.Empty>
          ) : (
            incidents.map((inc) => (
              <Table.Row
                key={inc.id}
                interactive
                onClick={() => navigate(`/incidents/${inc.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/incidents/${inc.id}`);
                }}
              >
                <Table.Cell tone="strong">{fmtDateTime(inc.occurred_at)}</Table.Cell>
                <Table.Cell>{CATEGORY_LABEL[inc.category]}</Table.Cell>
                <Table.Cell>
                  <SeverityBadge severity={inc.severity} />
                </Table.Cell>
                <Table.Cell tone="mono">{inc.report_number ?? '—'}</Table.Cell>
                <Table.Cell>
                  <StatusBadge status={inc.status} />
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>

      <Dialog
        title="Report incident"
        open={showNew}
        busy={createIncident.isPending}
        onClose={() => {
          if (!createIncident.isPending) {
            setShowNew(false);
            resetForm();
          }
        }}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowNew(false);
                resetForm();
              }}
              disabled={createIncident.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="new-incident-form"
              variant="primary"
              disabled={createIncident.isPending}
            >
              {createIncident.isPending ? 'Saving…' : 'Log incident'}
            </Button>
          </>
        }
      >
        <form id="new-incident-form" className="rpt-form" onSubmit={onCreate}>
          <FormRow>
            <FormField label="Category" required>
              <select
                className="form-input"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as IncidentCategory)}
                disabled={createIncident.isPending}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Severity">
              <select
                className="form-input"
                value={newSeverity}
                onChange={(e) => setNewSeverity(e.target.value as IncidentSeverity)}
                disabled={createIncident.isPending}
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </option>
                ))}
              </select>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Occurred at">
              <DateInput
                value={newOccurredAt}
                onChange={setNewOccurredAt}
                withTime
                disabled={createIncident.isPending}
              />
            </FormField>
            <FormField label="Report number">
              <Input
                type="text"
                value={newReportNumber}
                onChange={(e) => setNewReportNumber(e.target.value)}
                placeholder="Optional — e.g. agency case #"
                maxLength={120}
                disabled={createIncident.isPending}
              />
            </FormField>
          </FormRow>
          <FormField label="Narrative">
            <Textarea
              className="rpt-textarea"
              value={newNarrative}
              onChange={(e) => setNewNarrative(e.target.value)}
              placeholder="What happened, who was involved, actions taken…"
              maxLength={50_000}
              disabled={createIncident.isPending}
            />
          </FormField>
          <label className="rpt-checkbox">
            <input
              type="checkbox"
              checked={newPoliceNotified}
              onChange={(e) => setNewPoliceNotified(e.target.checked)}
              disabled={createIncident.isPending}
            />
            <span>Police / authorities notified</span>
          </label>
          {formError && (
            <p className="rpt-error" role="alert">
              {formError}
            </p>
          )}
        </form>
      </Dialog>
    </section>
  );
}
